import { supabase } from "@/lib/supabase/client";
import type { ProvisionCategory } from "@argus/engine";

export type MfnScope = "full" | "economic_only" | "restricted" | "observer" | "custom";
export type MfnFundScope = "this_fund_only" | "manager_funds" | "successor_funds" | "all_funds";
export type MfnTriggerStatus =
  | "open"
  | "reviewing"
  | "extending"
  | "negotiating"
  | "resolved_extended"
  | "resolved_excluded"
  | "resolved_negotiated"
  | "dismissed";

export const TRIGGER_STATUS_LABELS: Record<MfnTriggerStatus, string> = {
  open: "Open",
  reviewing: "Reviewing",
  extending: "Extending benefit",
  negotiating: "Negotiating",
  resolved_extended: "Resolved — extended",
  resolved_excluded: "Resolved — excluded",
  resolved_negotiated: "Resolved — negotiated",
  dismissed: "Dismissed",
};

export const RESOLVED_STATUSES: readonly MfnTriggerStatus[] = [
  "resolved_extended",
  "resolved_excluded",
  "resolved_negotiated",
  "dismissed",
];

export interface MfnClauseRow {
  id: string;
  tenant_id: string;
  provision_id: string;
  fund_id: string;
  lp_id: string;
  scope: MfnScope;
  scope_details: Record<string, unknown> | null;
  threshold_commitment_usd: number | null;
  excluded_categories: ProvisionCategory[];
  fund_scope: MfnFundScope;
}

const CLAUSE_COLS =
  "id, tenant_id, provision_id, fund_id, lp_id, scope, scope_details, threshold_commitment_usd, excluded_categories, fund_scope";

export interface UpsertMfnClauseInput {
  tenant_id: string;
  provision_id: string;
  fund_id: string;
  lp_id: string;
  scope: MfnScope;
  scope_details?: Record<string, unknown> | null;
  threshold_commitment_usd?: number | null;
  excluded_categories?: ProvisionCategory[];
  fund_scope?: MfnFundScope;
}

export async function upsertMfnClauses(rows: UpsertMfnClauseInput[]): Promise<void> {
  if (rows.length === 0) return;
  const { error } = await supabase.from("mfn_clauses").upsert(rows, { onConflict: "provision_id" });
  if (error) throw new Error(`upsertMfnClauses: ${error.message}`);
}

export async function listMfnClausesByFund(
  tenantId: string,
  fundId: string,
): Promise<MfnClauseRow[]> {
  // HITL gate (build doc §1.4 / migration 0010): a clause whose parent
  // provision has not been human-reviewed must not feed the MFN engine (run
  // evaluation, conflict radar, simulator). Inner-join on the FK
  // mfn_clauses.provision_id -> provisions.id and filter on the joined row so
  // the gate lives at the query level, not a second round-trip.
  const { data, error } = await supabase
    .from("mfn_clauses")
    .select(`${CLAUSE_COLS}, provision:provisions!inner(review_status)`)
    .eq("tenant_id", tenantId)
    .eq("fund_id", fundId)
    .in("provision.review_status", ["confirmed", "edited"]);
  if (error) throw new Error(`listMfnClausesByFund: ${error.message}`);
  return (data ?? []).map((row) => {
    const { provision: _provision, ...clause } = row as unknown as MfnClauseRow & {
      provision: { review_status: string };
    };
    return clause;
  });
}

export interface MfnTriggerRow {
  id: string;
  tenant_id: string;
  mfn_clause_id: string;
  triggering_provision_id: string;
  fund_id: string;
  holder_lp_id: string;
  beneficiary_lp_id: string;
  category: ProvisionCategory;
  rationale: string | null;
  confidence: number | null;
  status: MfnTriggerStatus;
  assigned_to: string | null;
  resolution_notes: string | null;
  resolved_at: string | null;
  created_at: string;
  updated_at: string;
}

const TRIGGER_COLS =
  "id, tenant_id, mfn_clause_id, triggering_provision_id, fund_id, holder_lp_id, beneficiary_lp_id, category, rationale, confidence, status, assigned_to, resolution_notes, resolved_at, created_at, updated_at";

export async function listMfnTriggers(
  tenantId: string,
  opts: { fundId?: string; openOnly?: boolean } = {},
): Promise<MfnTriggerRow[]> {
  let q = supabase
    .from("mfn_triggers")
    .select(TRIGGER_COLS)
    .eq("tenant_id", tenantId)
    .order("created_at", { ascending: false });
  if (opts.fundId) q = q.eq("fund_id", opts.fundId);
  if (opts.openOnly) q = q.not("status", "in", `(${RESOLVED_STATUSES.join(",")})`);
  const { data, error } = await q;
  if (error) throw new Error(`listMfnTriggers: ${error.message}`);
  return data ?? [];
}

export async function getMfnTrigger(id: string): Promise<MfnTriggerRow> {
  const { data, error } = await supabase
    .from("mfn_triggers")
    .select(TRIGGER_COLS)
    .eq("id", id)
    .single();
  if (error) throw new Error(`getMfnTrigger: ${error.message}`);
  return data;
}

export interface InsertMfnTriggerInput {
  tenant_id: string;
  mfn_clause_id: string;
  triggering_provision_id: string;
  fund_id: string;
  holder_lp_id: string;
  beneficiary_lp_id: string;
  category: ProvisionCategory;
  rationale: string;
  confidence: number;
}

/** Insert newly computed triggers; re-evaluations skip pairs that already exist. */
export async function insertMfnTriggers(rows: InsertMfnTriggerInput[]): Promise<number> {
  if (rows.length === 0) return 0;
  const { data, error } = await supabase
    .from("mfn_triggers")
    .upsert(rows, {
      onConflict: "mfn_clause_id,triggering_provision_id",
      ignoreDuplicates: true,
    })
    .select("id");
  if (error) throw new Error(`insertMfnTriggers: ${error.message}`);
  return data?.length ?? 0;
}

export async function updateMfnTrigger(
  id: string,
  userId: string,
  patch: { status: MfnTriggerStatus; resolution_notes?: string | null },
): Promise<void> {
  const resolved = RESOLVED_STATUSES.includes(patch.status);
  const { error } = await supabase
    .from("mfn_triggers")
    .update({
      ...patch,
      resolved_at: resolved ? new Date().toISOString() : null,
      resolved_by: resolved ? userId : null,
    })
    .eq("id", id);
  if (error) throw new Error(`updateMfnTrigger: ${error.message}`);
}

// =============================================================================
// Continuous MFN enforcement (migration 20260702200000 — mfn_evaluations +
// mfn_triggers.stale/stale_at/last_evaluation_id). Argus's demo tenant runs on
// live prod Supabase and this migration lands separately from this code, so
// every function below that touches the new table/columns is either (a) a
// thin throwing wrapper meant to be called from inside the orchestrator's own
// fail-soft guard (`continuous-mfn.ts`), or (b) fail-soft itself when it's
// read directly by a React Query hook in the UI — see each function's doc.
// =============================================================================

export type MfnEvaluationTriggerSource =
  | "provision_reviewed"
  | "commitment_changed"
  | "clause_changed"
  | "manual"
  | "scheduled";

export const TRIGGER_SOURCE_LABELS: Record<MfnEvaluationTriggerSource, string> = {
  provision_reviewed: "Provision reviewed",
  commitment_changed: "Commitment changed",
  clause_changed: "MFN clause changed",
  manual: "Manual run",
  scheduled: "Scheduled",
};

export interface MfnEvaluationRow {
  id: string;
  tenant_id: string;
  fund_id: string;
  engine_version: string;
  trigger_source: MfnEvaluationTriggerSource;
  source_ref: string | null;
  clause_count: number;
  candidate_count: number;
  computed_triggers: number;
  new_triggers: number;
  newly_stale: number;
  ran_by: string | null;
  ran_at: string;
}

const EVALUATION_COLS =
  "id, tenant_id, fund_id, engine_version, trigger_source, source_ref, clause_count, candidate_count, computed_triggers, new_triggers, newly_stale, ran_by, ran_at";

/** Logged at most once per page load — avoids console spam from a query that
 * re-fires on every focus/interval while the migration is unapplied. */
let warnedEvaluationsUnavailable = false;
function warnEvaluationsUnavailable(context: string, err: unknown): void {
  if (warnedEvaluationsUnavailable) return;
  warnedEvaluationsUnavailable = true;
  console.warn(
    `[continuous-mfn] mfn_evaluations unavailable (migration 20260702200000 not applied yet) — ${context}:`,
    err,
  );
}

/**
 * Fail-soft: returns [] before the continuous-enforcement migration lands
 * (the demo tenant runs on live prod and the migration ships separately).
 * Read directly by the "Continuous enforcement" history panel.
 */
export async function listMfnEvaluations(
  tenantId: string,
  opts: { fundId?: string; limit?: number } = {},
): Promise<MfnEvaluationRow[]> {
  try {
    let q = supabase
      .from("mfn_evaluations")
      .select(EVALUATION_COLS)
      .eq("tenant_id", tenantId)
      .order("ran_at", { ascending: false });
    if (opts.fundId) q = q.eq("fund_id", opts.fundId);
    if (opts.limit) q = q.limit(opts.limit);
    const { data, error } = await q;
    if (error) {
      warnEvaluationsUnavailable("listMfnEvaluations", error);
      return [];
    }
    return data ?? [];
  } catch (err) {
    warnEvaluationsUnavailable("listMfnEvaluations", err);
    return [];
  }
}

/**
 * Fail-soft: returns null before the migration lands or when no evaluation
 * has ever run. Powers the dashboard's "Continuous · last evaluated…" badge.
 */
export async function getLatestMfnEvaluation(tenantId: string): Promise<MfnEvaluationRow | null> {
  try {
    const { data, error } = await supabase
      .from("mfn_evaluations")
      .select(EVALUATION_COLS)
      .eq("tenant_id", tenantId)
      .order("ran_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) {
      warnEvaluationsUnavailable("getLatestMfnEvaluation", error);
      return null;
    }
    return data;
  } catch (err) {
    warnEvaluationsUnavailable("getLatestMfnEvaluation", err);
    return null;
  }
}

export interface InsertMfnEvaluationInput {
  tenant_id: string;
  fund_id: string;
  engine_version: string;
  trigger_source: MfnEvaluationTriggerSource;
  source_ref: string | null;
  clause_count: number;
  candidate_count: number;
  computed_triggers: number;
  new_triggers: number;
  newly_stale: number;
  ran_by: string | null;
}

/**
 * Throws on error (including "relation does not exist" pre-migration) — by
 * design. This is a low-level write meant to be called only from inside
 * `enforceMfnForFund`'s own try/catch (continuous-mfn.ts), which is where the
 * fail-soft guard lives for the whole insert+stale-update unit of work.
 */
export async function insertMfnEvaluation(input: InsertMfnEvaluationInput): Promise<string> {
  const { data, error } = await supabase
    .from("mfn_evaluations")
    .insert(input)
    .select("id")
    .single<{ id: string }>();
  if (error) throw new Error(`insertMfnEvaluation: ${error.message}`);
  return data.id;
}

export interface MfnTriggerDiffRow {
  id: string;
  mfn_clause_id: string;
  triggering_provision_id: string;
  status: MfnTriggerStatus;
  stale: boolean;
}

const DIFF_COLS = "id, mfn_clause_id, triggering_provision_id, status, stale";

/** Throws on error — same rationale as `insertMfnEvaluation` above; the
 * `stale` column doesn't exist pre-migration, which is exactly the case the
 * orchestrator's guard is there to catch. */
export async function listMfnTriggersForDiff(
  tenantId: string,
  fundId: string,
): Promise<MfnTriggerDiffRow[]> {
  const { data, error } = await supabase
    .from("mfn_triggers")
    .select(DIFF_COLS)
    .eq("tenant_id", tenantId)
    .eq("fund_id", fundId);
  if (error) throw new Error(`listMfnTriggersForDiff: ${error.message}`);
  return data ?? [];
}

/** Throws on error — see `insertMfnEvaluation`. */
export async function markMfnTriggersStale(ids: string[], evaluationId: string): Promise<void> {
  if (ids.length === 0) return;
  const { error } = await supabase
    .from("mfn_triggers")
    .update({ stale: true, stale_at: new Date().toISOString(), last_evaluation_id: evaluationId })
    .in("id", ids);
  if (error) throw new Error(`markMfnTriggersStale: ${error.message}`);
}

/** Throws on error — see `insertMfnEvaluation`. */
export async function markMfnTriggersRevived(ids: string[], evaluationId: string): Promise<void> {
  if (ids.length === 0) return;
  const { error } = await supabase
    .from("mfn_triggers")
    .update({ stale: false, stale_at: null, last_evaluation_id: evaluationId })
    .in("id", ids);
  if (error) throw new Error(`markMfnTriggersRevived: ${error.message}`);
}

export interface MfnTriggerWithStale extends MfnTriggerRow {
  stale: boolean;
  stale_at: string | null;
  last_evaluation_id: string | null;
}

const STALE_TRIGGER_COLS = `${TRIGGER_COLS}, stale, stale_at, last_evaluation_id`;

let warnedStaleColsUnavailable = false;
function warnStaleColsUnavailable(context: string, err: unknown): void {
  if (warnedStaleColsUnavailable) return;
  warnedStaleColsUnavailable = true;
  console.warn(
    `[continuous-mfn] mfn_triggers.stale unavailable (migration 20260702200000 not applied yet) — ${context}:`,
    err,
  );
}

/**
 * Fail-soft superset of `listMfnTriggers`: same rows, with `stale`/`stale_at`/
 * `last_evaluation_id` attached once the migration has landed. Falls back to
 * the base columns (stale: false for every row) so the mfn-list page renders
 * byte-identically pre-migration.
 */
export async function listMfnTriggersWithStale(
  tenantId: string,
  opts: { fundId?: string; openOnly?: boolean } = {},
): Promise<MfnTriggerWithStale[]> {
  let q = supabase
    .from("mfn_triggers")
    .select(STALE_TRIGGER_COLS)
    .eq("tenant_id", tenantId)
    .order("created_at", { ascending: false });
  if (opts.fundId) q = q.eq("fund_id", opts.fundId);
  if (opts.openOnly) q = q.not("status", "in", `(${RESOLVED_STATUSES.join(",")})`);
  const { data, error } = await q;
  if (error) {
    warnStaleColsUnavailable("listMfnTriggersWithStale", error);
    const fallback = await listMfnTriggers(tenantId, opts);
    return fallback.map((row) => ({
      ...row,
      stale: false,
      stale_at: null,
      last_evaluation_id: null,
    }));
  }
  return data ?? [];
}

/** Fail-soft superset of `getMfnTrigger` — see `listMfnTriggersWithStale`. */
export async function getMfnTriggerWithStale(id: string): Promise<MfnTriggerWithStale> {
  const { data, error } = await supabase
    .from("mfn_triggers")
    .select(STALE_TRIGGER_COLS)
    .eq("id", id)
    .single();
  if (error) {
    warnStaleColsUnavailable("getMfnTriggerWithStale", error);
    const fallback = await getMfnTrigger(id);
    return { ...fallback, stale: false, stale_at: null, last_evaluation_id: null };
  }
  return data;
}
